import * as React from 'react';
import styles from './SerialBundlePage.scss';
import equal from 'fast-deep-equal/react';
import { useSelector } from 'react-redux';
import { ICurrentPage } from '~/Actions/ActionAppType';
import { State } from '~/Reducers';
import classNames from 'classnames';
import useMaskScrollEffect from '~/hooks/useMaskScrollEffect';
import useDelegateContainerScroll from '~/hooks/useDelegateContainerScroll';
import SerialItemsSmallCarousel from '~/components/SerialItemsSmallCarousel/SerialItemsSmallCarousel';
import { getAvailabelSerialBundleForPurchase, getSerialBundlesById } from '~/utils/bundles';
import BundlePageLabels from '~/Layouts/BundlePage/BundlePageLabels/BundlePageLabels';
import BundlePageHeader from '~/Layouts/BundlePage/BundlePageHeader/BundlePageHeader';
import BundlePageDescription from '~/Layouts/BundlePage/BundlePageDescription/BundlePageDescription';
import SerialBundleFooter from './SerialBundleFooter';
import { BundleLimitContainerWidget } from '../BundlePage/BundlePageHeader/content';

interface ISerialBundlePage {
    bundle: IBundle;
}

interface IStateSelector {
    accountId: number;
    restrictedLootboxesPurchaseCount?: AccountRestrictedLootboxesPurchaseCount;
    bundles: IBundleList;
    activeCouponId?: number;
    coupons: ICoupon[];
    disabledCategories?: string[];
    currentPage: ICurrentPage;
    purchasedLimitedBundles?: AccountPurchasedLimitedBundles;
    port?: IPort;
    categories: ICategories;
    balance: IBalance;
    bundlesInTransaction: number[];
}

const stateSelector = (state: State): IStateSelector => {
    return {
        currentPage: state.ReducerApp.currentPage,
        accountId: state.ReducerAccount?.id,
        restrictedLootboxesPurchaseCount: state.ReducerAccount.restrictedLootboxesPurchaseCount,
        bundles: state.ReducerApp.bundles,
        activeCouponId: state.ReducerAccount.activeCouponId,
        coupons: state.ReducerAccount.coupons,
        disabledCategories: state.ReducerApp.disabledCategories,
        purchasedLimitedBundles: state.ReducerAccount.purchasedLimitedBundles,
        port: state.ReducerApp.port,
        categories: state.ReducerApp.categories,
        balance: state.ReducerAccount.balance,
        bundlesInTransaction: state.ReducerAccount.bundlesInTransaction,
    };
};

const SerialBundlePage = ({ bundle }: ISerialBundlePage) => {
    const state = useSelector<State, IStateSelector>(stateSelector, equal);
    const refContent = React.useRef<HTMLDivElement>(null);
    const sequence = getSerialBundlesById(state.bundles, bundle.id);

    useMaskScrollEffect(refContent);
    useDelegateContainerScroll(refContent);

    const availableBundle = getAvailabelSerialBundleForPurchase(sequence, state.purchasedLimitedBundles);

    return (
        <div className={classNames(styles.container, 'armory__auto--bundle-page_wrapper')} data-bundle-id={bundle.id}>
            <div className={styles.carousel}>
                <SerialItemsSmallCarousel bundle={bundle} sequence={sequence} availableBundle={availableBundle} />
            </div>
            <div className={styles.content} ref={refContent}>
                <div className={styles.bundleContent}>
                    <BundlePageLabels bundle={bundle} />
                    <BundlePageHeader bundle={bundle} bundles={state.bundles} renderPreviewContent={true} />
                    <BundleLimitContainerWidget bundle={bundle} className={styles.limitWidget} />
                    <div className={styles.mobileImage}>
                        <img src={bundle.icons?.big} />
                    </div>
                    <BundlePageDescription bundle={bundle} />
                </div>
            </div>
            <SerialBundleFooter
                sequence={sequence}
                currentPage={state.currentPage}
                isAuthorized={!!state.accountId}
                bundle={bundle}
                purchasedLimitedBundles={state.purchasedLimitedBundles}
                restrictedLootboxesPurchaseCount={state.restrictedLootboxesPurchaseCount}
                balance={state.balance}
                bundlesInTransaction={state.bundlesInTransaction || []}
                bundles={state.bundles}
            />
        </div>
    );
};

export default SerialBundlePage;
